import { Button, Stack } from '@mui/material_modern'
import { Link } from 'react-router-dom'
import { AddCircle as AddCircleIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material_modern'
import store from 'state/store.ts'
import { showFormModal } from 'state/ui/index.ts'
import SubFormLayout from 'components/layouts/SubFormLayout'
import { useFormContext } from 'react-hook-form'
import { DT_STATUS_ENUM } from 'utils/constants.tsx'
import { useAppSelector } from '../../../../utils/custom-hooks/useTypedReduxStore.ts'
import { useEffect } from 'react'

interface CustomerInfoFormProps {
  customerId: string | null
  ticketHasAssets: boolean
  setValue: (field: string, value: any, options?: any) => void
  setCustomerValue: (customer?: any) => void
  locationOptions: Array<{ id: string; name: string }>
  contactOptions: Array<{ id: string; name: string }>
  setLocation: (location: { id: string; name: string }) => void
  supplierTypeIds?: string[]
}

const CustomerInfoForm: React.FC<CustomerInfoFormProps> = ({
  customerId,
  ticketHasAssets,
  setValue,
  setCustomerValue,
  locationOptions,
  contactOptions,
  setLocation,
  supplierTypeIds,
}) => {
  const { enableDispatchContact } = useAppSelector(state => state.featureFlags?.variables || {})

  const { watch } = useFormContext()

  const customerWatch = watch('customer')

  const ticketStatus = watch('status')
  const disableCustomerEdit =
    [DT_STATUS_ENUM.IN_PROGRESS, DT_STATUS_ENUM.COMPLETED, DT_STATUS_ENUM.FAILED].includes(ticketStatus) ||
    ticketHasAssets
  const isRequested = ticketStatus === DT_STATUS_ENUM.REQUESTED

  const supplierTypeIdsFilter = !!supplierTypeIds && supplierTypeIds?.length > 0 ? { supplierTypeIds } : undefined

  const intercompany = watch('intercompany')

  useEffect(() => {
    if (intercompany && customerWatch?.data && !customerWatch.data.isYard) {
      setCustomerValue(null)
    }
  }, [intercompany, customerWatch?.data?.id])

  return (
    <SubFormLayout
      formName="Customer Information"
      boxStyles={{ width: '50%' }}
      fields={[
        customerId
          ? [
              {
                name: 'customer.displayName',
                label: 'Customer Name',
                dataTest: 'customer-name',
                fieldType: 'static',
              },
              {
                name: 'customer.externalId',
                label: 'Customer Id',
                dataTest: 'customer-external-id',
                fieldType: 'static',
              },
              {
                name: 'customer.supplierType',
                label: 'Type',
                dataTest: 'customer-supplier-type',
                fieldType: 'static',
              },
              {
                name: 'buttons',
                label: '',
                fieldType: 'custom',
                dataTest: 'buttons',
                customComponent: () => (
                  <Stack spacing={1} direction="row" justifyContent="flex-start" alignItems="baseline">
                    <Link
                      to={`/app/customers/${customerId}`}
                      target="_blank"
                      style={{ textDecoration: 'none', marginTop: 1 }}
                    >
                      <Button startIcon={<OpenInNewIcon />}>View</Button>
                    </Link>
                    <Button
                      variant="text"
                      color={ticketHasAssets ? 'inherit' : 'error'}
                      onClick={() => setCustomerValue()}
                      disabled={disableCustomerEdit || isRequested}
                    >
                      Remove
                    </Button>
                  </Stack>
                ),
              },
            ]
          : [
              {
                name: 'customer.data',
                label: 'Customer Name',
                dataTest: 'customer',
                defaultValue: null,
                fieldType: 'customerAutocomplete',
                onChange: customer => setCustomerValue(customer),
                quickAdd: true,
                disabled: disableCustomerEdit || isRequested,
                baseFilter: supplierTypeIdsFilter,
                intercompany: intercompany,
              },
            ],
        [
          ...(enableDispatchContact
            ? [
                {
                  name: 'contactId',
                  label: 'Contact',
                  dataTest: 'customer-contact',
                  fieldType: 'autocomplete',
                  size: 'small',
                  options: contactOptions,
                  getOptionLabel: option => option.name || '',
                  onChange: (event, value) => {
                    if (!customerId || disableCustomerEdit || isRequested || !value) return
                    setValue('contactId', value.id, { shouldDirty: true })
                    event?.target?.blur()
                  },
                  defaultValue: contactOptions?.[0]?.id || null,
                  disabled: !customerId || disableCustomerEdit || isRequested,
                  disableClearable: true,
                  sideButton: {
                    variant: 'text',
                    disabled: !customerId || disableCustomerEdit || isRequested,
                    startIcon: <AddCircleIcon />,
                    onClick: () =>
                      store.dispatch(
                        showFormModal({
                          formModalName: 'new-customer-modal',
                          formModalProps: { type: 'contacts', customerId },
                        }),
                      ),
                  },
                },
              ]
            : []),
          {
            name: 'location.id',
            label: 'Location',
            dataTest: 'customer-location',
            fieldType: 'autocomplete',
            size: 'small',
            options: locationOptions,
            getOptionLabel: option => option.name || '',
            onChange: (_, value) => {
              if (!customerId || disableCustomerEdit || isRequested || !value) return
              setLocation(value)
            },
            defaultValue: locationOptions?.[0] || null,
            disabled: !customerId || disableCustomerEdit || isRequested,
            disableClearable: true,
            sideButton: {
              variant: 'text',
              disabled: !customerId || disableCustomerEdit || isRequested,
              startIcon: <AddCircleIcon />,
              onClick: () =>
                store.dispatch(
                  showFormModal({
                    formModalName: 'new-customer-modal',
                    formModalProps: { type: 'locations', customerId },
                  }),
                ),
            },
          },
          {
            name: 'location.address',
            label: 'Address',
            dataTest: 'location-address',
            fieldType: 'input',
            disabled: true,
          },
        ],
        [
          {
            name: 'location.city',
            label: 'City',
            dataTest: 'location-city',
            fieldType: 'input',
            disabled: true,
          },
          {
            name: 'location.state',
            label: 'State',
            dataTest: 'location-state',
            fieldType: 'input',
            disabled: true,
          },
          {
            name: 'location.zip',
            label: 'Zip',
            dataTest: 'location-zip',
            fieldType: 'input',
            disabled: true,
          },
        ],
      ]}
    />
  )
}

export default CustomerInfoForm
